import React, { useState } from 'react';
import {
  KeyIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  PencilIcon,
  ClipboardDocumentIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import {
  updateCompanyProfile,
  regenerateInviteCode
} from '../../utils/companyClient';

const STATE_OPTIONS = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

const SIZE_OPTIONS = ['1-10', '11-50', '51-200', '201-500', '500+'];

export default function CompanySettingsTab({
  company,
  userRole,
  onRefresh
}) {
  const [formData, setFormData] = useState({
    name: company?.name || '',
    description: company?.description || '',
    website: company?.website || '',
    state: company?.state || '',
    size: company?.size || ''
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRegeneratingCode, setIsRegeneratingCode] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copiedCode, setCopiedCode] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!company?.id) return;

    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      const updates = Object.keys(formData).reduce((acc, key) => {
        if (formData[key] !== company[key]) {
          acc[key] = formData[key];
        }
        return acc;
      }, {});

      if (Object.keys(updates).length === 0) {
        setIsEditing(false);
        return;
      }

      await updateCompanyProfile(company.id, updates);

      setIsEditing(false);
      setSuccess('Settings saved');
      if (onRefresh) onRefresh();
      setTimeout(() => setSuccess(''), 3000);

    } catch (error) {
      console.error('Error saving settings:', error);
      setError(error.message || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: company?.name || '',
      description: company?.description || '',
      website: company?.website || '',
      state: company?.state || '',
      size: company?.size || ''
    });
    setIsEditing(false);
    setError('');
  };

  const handleRegenerateInviteCode = async () => {
    if (!company?.id || !window.confirm('Are you sure? The current invite code will become invalid.')) {
      return;
    }

    setIsRegeneratingCode(true);
    setError('');

    try {
      await regenerateInviteCode(company.id);
      setSuccess('New invite code generated');
      if (onRefresh) onRefresh();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error regenerating invite code:', error);
      setError(error.message || 'Failed to regenerate');
    } finally {
      setIsRegeneratingCode(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(company?.invite_code || '');
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 3000);
  };

  const inputClass = "w-full px-3 py-2 border border-gray-300 rounded-sm text-sm focus:ring-1 focus:ring-red-500 focus:border-red-500";
  const selectClass = "w-full px-3 py-2 border border-gray-300 rounded-sm text-sm focus:ring-1 focus:ring-red-500 focus:border-red-500 bg-white";

  return (
    <div>
      {/* Success/Error Messages */}
      {(success || error) && (
        <div className={`px-6 py-3 border-b text-sm flex items-center ${
          success ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          {success ? <CheckIcon className="h-4 w-4 mr-2" /> : <ExclamationTriangleIcon className="h-4 w-4 mr-2" />}
          {success || error}
        </div>
      )}

      {/* Company Information */}
      <div className="bg-white border-b border-gray-200">
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Company Information</h2>
            <p className="text-sm text-gray-500 mt-0.5">Basic details about your organization</p>
          </div>
          {userRole === 'owner' && (
            <div className="flex items-center gap-2">
              {isEditing ? (
                <>
                  <button
                    onClick={handleCancel}
                    disabled={isSaving}
                    className="px-3 py-1.5 text-sm font-medium text-gray-600 border border-gray-300 rounded-sm hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-sm hover:bg-red-700 disabled:opacity-50"
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-600 border border-gray-300 rounded-sm hover:bg-gray-50"
                >
                  <PencilIcon className="h-3.5 w-3.5 mr-1.5" />
                  Edit
                </button>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            {/* Company Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Company Name
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={inputClass}
                  required
                />
              ) : (
                <p className="text-sm text-gray-900">{formData.name || '—'}</p>
              )}
            </div>

            {/* Website */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Website
              </label>
              {isEditing ? (
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  className={inputClass}
                  placeholder="https://example.com"
                />
              ) : (
                <p className="text-sm text-gray-900">
                  {formData.website ? (
                    <a href={formData.website} target="_blank" rel="noopener noreferrer" className="text-red-600 hover:underline">
                      {formData.website}
                    </a>
                  ) : '—'}
                </p>
              )}
            </div>

            {/* State */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                State
              </label>
              {isEditing ? (
                <select
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  className={selectClass}
                >
                  <option value="">Select state</option>
                  {STATE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              ) : (
                <p className="text-sm text-gray-900">{formData.state || '—'}</p>
              )}
            </div>

            {/* Company Size */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Company Size
              </label>
              {isEditing ? (
                <select
                  value={formData.size}
                  onChange={(e) => handleInputChange('size', e.target.value)}
                  className={selectClass}
                >
                  <option value="">Select size</option>
                  {SIZE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt} employees</option>)}
                </select>
              ) : (
                <p className="text-sm text-gray-900">{formData.size ? `${formData.size} employees` : '—'}</p>
              )}
            </div>

            {/* Description - Full Width */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Description
              </label>
              {isEditing ? (
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={3}
                  className={inputClass}
                  placeholder="A brief description of your company..."
                />
              ) : (
                <p className="text-sm text-gray-900">{formData.description || '—'}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Invite Code */}
      <div className="bg-white border-b border-gray-200">
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Invite Code</h2>
            <p className="text-sm text-gray-500 mt-0.5">Share this code to invite new team members</p>
          </div>
          {userRole === 'owner' && (
            <button
              onClick={handleRegenerateInviteCode}
              disabled={isRegeneratingCode}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-600 border border-gray-300 rounded-sm hover:bg-gray-50 disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-3.5 w-3.5 mr-1.5 ${isRegeneratingCode ? 'animate-spin' : ''}`} />
              {isRegeneratingCode ? 'Regenerating...' : 'Regenerate'}
            </button>
          )}
        </div>

        <div className="px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex-1 flex items-center justify-between bg-gray-50 border border-gray-200 rounded-sm px-4 py-3">
              <code className="text-lg font-mono font-semibold text-gray-900 tracking-wider select-all">
                {company?.invite_code || 'XXXX-XXXX-XXXX'}
              </code>
              <button
                onClick={copyToClipboard}
                className={`p-1.5 rounded transition-colors ${
                  copiedCode
                    ? 'bg-green-100 text-green-700'
                    : 'hover:bg-gray-200 text-gray-500'
                }`}
                title={copiedCode ? 'Copied!' : 'Copy to clipboard'}
              >
                {copiedCode ? (
                  <CheckIcon className="h-4 w-4" />
                ) : (
                  <ClipboardDocumentIcon className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            This code is valid until regenerated. New members join with default role.
          </p>
        </div>
      </div>
    </div>
  );
}
